import React, { useState, useEffect } from "react";
import {
    Dialog,
    DialogTitle,
    DialogContent,
    Box,
    Typography,
    Stack,
} from "@mui/material";
import { useSelector } from "react-redux";
import { AxiosResponse, AxiosError } from "axios";
import { FaCheck, FaTimes } from "react-icons/fa";
import EnhancedAxios from "../../axios/EnhancedAxios";
import { errorAlert } from "../../utils/alerts/alerts";
import PaginationFilter from "../filter/PaginationFilter";
import ThreeDotsLoader from "../loader/ThreeDotsLoader";

interface UserGift {
    user_id: string;
    sas_username: string;
    sas_id: number;
    is_used: boolean;
    used_at?: string;
    expires_at?: string;
    created_at: string;
}

interface GiftUserGiftsModalProps {
    open: boolean;
    onClose: () => void;
    giftId: number | string;
}

const GiftUserGiftsModal = ({
    open,
    onClose,
    giftId,
}: GiftUserGiftsModalProps) => {
    const userToken = useSelector((state: any) => state.user.userToken);
    const [currentPage, setCurrentPage] = useState(1);
    const [userGifts, setUserGifts] = useState<UserGift[]>([]);
    const [loading, setLoading] = useState(false);
    const [totalItems, setTotalItems] = useState(0);
    const itemsPerPage = 25;

    // Reset to page 1 when modal opens/closes
    useEffect(() => {
        if (open) {
            setCurrentPage(1);
        }
    }, [open]);

    // Fetch user gifts from API
    useEffect(() => {
        if (open && giftId) {
            setLoading(true);
            EnhancedAxios(null, userToken)
                .get(
                    `/admin/gifts/${giftId}/giftusers?page=${currentPage}&page_size=${itemsPerPage}`
                )
                .then((res: AxiosResponse) => {
                    setUserGifts(res.data.users || []);
                    setTotalItems(res.data.pagination?.total || 0);
                    setLoading(false);
                })
                .catch((error: AxiosError) => {
                    errorAlert(error);
                    setLoading(false);
                });
        }
    }, [open, giftId, currentPage, userToken]);

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="md"
            fullWidth
            sx={{
                "& .MuiPaper-root": {
                    minWidth: { xs: "290px", sm: "600px" },
                    maxHeight: "80vh",
                },
            }}
        >
            <DialogTitle>
                <Typography variant="h6" component="div">
                    المستخدمين {!loading && `(${totalItems})`}
                </Typography>
            </DialogTitle>

            <DialogContent>
                <Stack spacing={2} sx={{ mt: 1 }}>
                    {loading ? (
                        <Box
                            sx={{
                                display: "flex",
                                justifyContent: "center",
                                alignItems: "center",
                                minHeight: "200px",
                            }}
                        >
                            <ThreeDotsLoader />
                        </Box>
                    ) : userGifts.length > 0 ? (
                        <>
                            <Stack spacing={1}>
                                {userGifts.map((userGift) => (
                                    <Box
                                        key={userGift.user_id}
                                        sx={{
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "space-between",
                                            p: 1.5,
                                            border: "1px solid #e0e0e0",
                                            borderRadius: 1,
                                            backgroundColor: userGift.is_used
                                                ? "#f8f9fa"
                                                : "#fff",
                                        }}
                                    >
                                        <Typography variant="body2">
                                            {userGift.sas_username}
                                        </Typography>
                                        <Box
                                            sx={{
                                                display: "flex",
                                                alignItems: "center",
                                                gap: 0.5,
                                            }}
                                        >
                                            {userGift.is_used ? (
                                                <>
                                                    <FaCheck
                                                        style={{
                                                            color: "green",
                                                            fontSize: "12px",
                                                        }}
                                                    />
                                                    <Typography
                                                        variant="body2"
                                                        color="success.main"
                                                        sx={{
                                                            fontSize: "12px",
                                                        }}
                                                    >
                                                        مستخدم
                                                    </Typography>
                                                </>
                                            ) : (
                                                <>
                                                    <FaTimes
                                                        style={{
                                                            color: "red",
                                                            fontSize: "12px",
                                                        }}
                                                    />
                                                    <Typography
                                                        variant="body2"
                                                        color="error.main"
                                                        sx={{
                                                            fontSize: "12px",
                                                        }}
                                                    >
                                                        غير مستخدم
                                                    </Typography>
                                                </>
                                            )}
                                        </Box>
                                    </Box>
                                ))}
                            </Stack>

                            {totalItems > itemsPerPage && (
                                <PaginationFilter
                                    totalItems={totalItems}
                                    itemsPerPage={itemsPerPage}
                                    currentPage={currentPage}
                                    setCurrentPage={setCurrentPage}
                                />
                            )}
                        </>
                    ) : (
                        <Box sx={{ textAlign: "center", py: 4 }}>
                            <Typography variant="body1" color="text.secondary">
                                لا يوجد مستخدمين متاحين
                            </Typography>
                        </Box>
                    )}
                </Stack>
            </DialogContent>
        </Dialog>
    );
};

export default GiftUserGiftsModal;

